import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  TrendingUp, 
  Users, 
  FileText, 
  Package,
  Activity,
  Upload,
  Download,
  Plus,
  ArrowRight
} from 'lucide-react';

const DashboardHome = ({ user, onNavigate }) => {
  const [dashboardData, setDashboardData] = useState({
    pmCounts: {
      totalPMs: 0,
      currentMonthPMs: 0,
      overduePMs: 0,
      completedPMs: 0,
      processedPMs: 0,
      inventory: 0
    },
    recentActivity: [],
    locationSummary: {
      locations: [],
      summary: {}
    },
    loading: true
  });

  const [timeframe, setTimeframe] = useState('this-month');

  // Drop a single-use filter into sessionStorage and navigate to PM
  // Management. PMManagement reads and clears the key on mount.
  // adhesive_cleaned: 'yes' | 'no' | 'na' | ''  ('' = no adhesive filter,
  // just this-month completions).
  const handleAdhesiveDrilldown = (adhesive_cleaned) => {
    const closed_month = dashboardData?.pmCounts?.adhesive?.closed_month || '';
    try {
      sessionStorage.setItem('pmplus_pending_pm_filter', JSON.stringify({
        adhesive_cleaned,
        closed_month,
        status: 'completed'   // scope the drill-down to completed PMs
      }));
    } catch (_) { /* storage disabled — click still navigates */ }
    if (onNavigate) onNavigate('natch-pm');
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setDashboardData(prev => ({ ...prev, loading: true }));

        // Fetch PM counts
        const countsResponse = await fetch('/api/dashboard/counts');
        const pmCounts = await countsResponse.json();

        // Fetch recent activity (latest processed PMs)
        const activityResponse = await fetch('/api/pms?filter=processed&limit=10');
        const activityData = await activityResponse.json();

        // Fetch location summary
        const locationResponse = await fetch('/api/pms/locations-summary');
        const locationData = await locationResponse.json();

        setDashboardData({
          pmCounts: pmCounts,
          recentActivity: activityData.data || [],
          locationSummary: locationData,
          loading: false
        });

      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        setDashboardData(prev => ({ ...prev, loading: false }));
      }
    };

    fetchDashboardData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate metrics
  const calculateCompletionRate = () => {
    const { completedPMs, processedPMs, currentMonthPMs } = dashboardData.pmCounts;
    const totalCompleted = completedPMs + processedPMs;
    const totalThisMonth = currentMonthPMs + totalCompleted;
    return totalThisMonth > 0 ? Math.round((totalCompleted / totalThisMonth) * 100) : 0;
  };

  const getUrgencyColor = (count, type) => {
    if (type === 'overdue' && count > 0) return 'text-red-600 bg-red-50 border-red-200';
    if (type === 'current' && count > 20) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  // Metric cards data
  const metricCards = [
    {
      title: 'Total Open PMs',
      value: dashboardData.pmCounts.totalPMs,
      icon: FileText,
      color: 'text-blue-600 bg-blue-50 border-blue-200',
      trend: '+2 from last week'
    },
    {
      title: 'This Month',
      value: dashboardData.pmCounts.currentMonthPMs,
      icon: Calendar,
      color: getUrgencyColor(dashboardData.pmCounts.currentMonthPMs, 'current'),
      trend: 'Due this month'
    },
    {
      title: 'Overdue',
      value: dashboardData.pmCounts.overduePMs,
      icon: AlertTriangle,
      color: getUrgencyColor(dashboardData.pmCounts.overduePMs, 'overdue'),
      trend: dashboardData.pmCounts.overduePMs > 0 ? 'Needs attention' : 'All current'
    },
    {
      title: 'Completion Rate',
      value: `${calculateCompletionRate()}%`,
      icon: TrendingUp,
      color: calculateCompletionRate() > 80 ? 'text-green-600 bg-green-50 border-green-200' : 'text-yellow-600 bg-yellow-50 border-yellow-200',
      trend: 'This month'
    },
    {
      title: 'Inventory Items',
      value: dashboardData.pmCounts.inventory,
      icon: Package,
      color: 'text-purple-600 bg-purple-50 border-purple-200',
      trend: 'Total tracked'
    },
    {
      title: 'Processed',
      value: dashboardData.pmCounts.processedPMs,
      icon: CheckCircle,
      color: 'text-green-600 bg-green-50 border-green-200',
      trend: 'Completed workflow'
    }
  ];

  // Quick actions
  const quickActions = [
    { title: 'Upload PM CSV', icon: Upload, action: () => onNavigate('natch-pm'), color: '#8b5cf6' },
    { title: 'View All PMs', icon: FileText, action: () => onNavigate('natch-pm'), color: '#7c3aed' },
    { title: 'Manage Inventory', icon: Package, action: () => onNavigate('nrmc-inventory'), color: '#6d28d9' },
    { title: 'Export Reports', icon: Download, action: () => {}, color: '#5b21b6' }
  ];

  if (dashboardData.loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #f3f4f6',
          borderTop: '4px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }}></div>
        <p style={{ color: '#6b7280' }}>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
          Welcome back, {user?.firstName || user?.fullName || 'User'}! 👋
        </h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>
          Here's your PM management overview for {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Metrics Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        {metricCards.map((card, index) => {
          const IconComponent = card.icon;
          return (
            <div key={index} style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 10px 25px 0 rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div style={{
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid'
                }} className={card.color}>
                  <IconComponent size={24} />
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937' }}>
                    {card.value}
                  </div>
                </div>
              </div>
              <div>
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                  {card.title}
                </h3>
                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {card.trend}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Adhesive Tracking Card — this month's PM completion breakdown by
          adhesive-residue answer. Each row is a drill-down click. */}
      {(() => {
        const a = dashboardData?.pmCounts?.adhesive || { total: 0, yes: 0, na: 0, no: 0, null_count: 0 };
        const StatRow = ({ label, value, color, target }) => (
          <div
            onClick={() => handleAdhesiveDrilldown(target)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.75rem 1rem', borderRadius: '0.5rem',
              background: '#f9fafb', border: '1px solid #e5e7eb',
              cursor: 'pointer', transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
          >
            <span style={{ fontSize: '0.9rem', color: '#374151', fontWeight: 500 }}>{label}</span>
            <span style={{ fontSize: '1.15rem', fontWeight: 700, color: color }}>{value}</span>
          </div>
        );
        return (
          <div style={{
            background: 'white', border: '1px solid #e5e7eb', borderRadius: '0.75rem',
            padding: '1.5rem', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)', marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1f2937' }}>
                Adhesive Cleaning — This Month
              </h3>
              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Click any row to drill in</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem' }}>
              <StatRow label="PMs Completed This Month" value={a.total} color="#1f2937" target="" />
              <StatRow label="With Adhesive Cleaned"    value={a.yes}   color="#047857" target="yes" />
              <StatRow label="Marked N/A"               value={a.na}    color="#4b5563" target="na" />
              <StatRow label="Not Cleaned"              value={a.no}    color="#b91c1c" target="no" />
            </div>
            {a.null_count > 0 && (
              <div style={{ marginTop: '0.65rem', fontSize: '0.8rem', color: '#6b7280' }}>
                {a.null_count} completion{a.null_count === 1 ? '' : 's'} pre-date the adhesive question — no answer recorded.
              </div>
            )}
          </div>
        );
      })()}

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
        
        {/* Location Breakdown */}
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
              PM Status by Location
            </h2>
            <button 
              onClick={() => onNavigate('natch-pm')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              View All <ArrowRight size={16} />
            </button>
          </div>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {dashboardData.locationSummary.locations?.slice(0, 8).map((location, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem 0',
                borderBottom: index < 7 ? '1px solid #f3f4f6' : 'none'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', color: '#1f2937' }}>{location.location}</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {location.total_open} total open
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2563eb' }}>
                      {location.current_month}
                    </div>
                    <div style={{ fontSize: '0.625rem', color: '#6b7280' }}>This Month</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: location.overdue > 0 ? '#dc2626' : '#10b981' }}>
                      {location.overdue}
                    </div>
                    <div style={{ fontSize: '0.625rem', color: '#6b7280' }}>Overdue</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions & Recent Activity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Quick Actions */}
          <div style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
              Quick Actions
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {quickActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '1rem',
                      background: action.color,
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    <IconComponent size={20} style={{ marginBottom: '0.5rem' }} />
                    {action.title}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recent Activity */}
          <div style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
              Recent Activity
            </h2>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {dashboardData.recentActivity.slice(0, 5).map((pm, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 0',
                  borderBottom: index < 4 ? '1px solid #f3f4f6' : 'none'
                }}>
                  <div style={{
                    padding: '0.5rem',
                    background: '#10b981',
                    borderRadius: '50%',
                    color: 'white'
                  }}>
                    <CheckCircle size={16} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                      {pm.number}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {pm.type} - {pm.work_location}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.625rem', color: '#6b7280', textAlign: 'right' }}>
                    Processed
                  </div>
                </div>
              ))}
              {dashboardData.recentActivity.length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  No recent activity
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* System Status Bar */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              System Overview
            </h3>
            <p style={{ fontSize: '0.875rem', opacity: 0.9 }}>
              {dashboardData.locationSummary.summary?.total_locations || 0} locations • {dashboardData.pmCounts.totalPMs} active PMs • {calculateCompletionRate()}% completion rate
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Activity size={20} />
            <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>All Systems Operational</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;